import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Server, X } from 'lucide-react';
import {
  apiService, ConfigInspectStatus, ContainerApplyEntry, ContainerApplyResult, ContainerApplyStatus, PackContainer,
} from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import styles from './AllContainers.module.scss';

// ============================================================
// Config commune à tous les containers d'un projet
// ============================================================
// Lootbox, Jobs, RecubeJobs, Armes, Shop admin, Factions et les réglages de grade (chat, boutique, apparence) ne se
// règlent plus container par container : la page lit un container de RÉFÉRENCE, le premier (par id)
// qui porte la config, et l'enregistrement écrit dans chaque container du projet qui la porte. Ce
// fichier regroupe ce que ces pages partagent : l'ordre stable des containers, le bandeau qui dit où
// la config est lue et quels containers en diffèrent, et le rapport container par container.
// ============================================================

const NO_CONTAINERS: PackContainer[] = [];

/** Ordre stable, le même que le serveur (par id) : la référence est le premier container qui porte la config. */
export function sortContainers(list: PackContainer[]): PackContainer[] {
  return [...list].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Containers du projet actif, dans l'ordre stable. `loaded` reste faux tant que la liste du projet n'est pas arrivée. */
export function useProjectContainers(): { containers: PackContainer[]; loaded: boolean } {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [state, setState] = useState<{ project: string; containers: PackContainer[] } | null>(null);

  useEffect(() => {
    let active = true;
    apiService.getContainers().then((r) => {
      if (!active) return;
      const list = (r.data?.containers ?? []).filter((c) => (c.project ?? 'paladium') === activeGameId);
      setState({ project: activeGameId, containers: sortContainers(list) });
    });
    return () => { active = false; };
  }, [activeGameId]);

  const loaded = state?.project === activeGameId;
  return { containers: loaded && state ? state.containers : NO_CONTAINERS, loaded };
}

/**
 * Donnée lue dans CHAQUE container (config essentials, config premium...), rechargée quand la liste
 * des containers ou `reloadKey` change. `data` vaut null tant que la liste courante n'a pas été lue
 * une première fois ; un rechargement garde la lecture précédente affichée jusqu'à la suivante.
 */
export function useContainersData<T>(
  containers: PackContainer[],
  fetchOne: (containerId: string) => Promise<T>,
  reloadKey: unknown,
): { data: Record<string, T> | null; loading: boolean } {
  const fetchRef = useRef(fetchOne);
  fetchRef.current = fetchOne;
  const [state, setState] = useState<{ of: PackContainer[]; data: Record<string, T> } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all(containers.map(async (c) => [c.id, await fetchRef.current(c.id)] as const)).then((pairs) => {
      if (!active) return;
      setState({ of: containers, data: Object.fromEntries(pairs) as Record<string, T> });
      setLoading(false);
    });
    return () => { active = false; };
  }, [containers, reloadKey]);

  // Lecture d'une autre liste (projet précédent) : jamais présentée comme celle des containers courants.
  return { data: state && state.of === containers ? state.data : null, loading };
}

/** Un container tel que le bandeau le présente. */
export interface ScopeEntry {
  containerId: string;
  containerName: string;
  status: ConfigInspectStatus;
  /** Sections ou champs qui diffèrent de la référence (statut DIFFERENT). */
  differs?: string[];
}

const SKIP_LABEL: Partial<Record<ConfigInspectStatus, string>> = {
  SKIPPED_NOT_SYNCED: 'non synchronisé',
  SKIPPED_FILE_MISSING: 'config absente',
  SKIPPED_AMBIGUOUS: 'plusieurs fichiers candidats',
  SKIPPED_INVALID_JSON: 'JSON invalide',
  SKIPPED_MOD_ABSENT: "mod absent de l'image",
  SKIPPED_MOD_SHARED: 'fichier lu par deux mods',
  SKIPPED_MOD_UNKNOWN: 'mod non identifié',
  FOUND: 'non comparé',
  FAILED: 'illisible',
};

const CARRIES: ConfigInspectStatus[] = ['REFERENCE', 'SAME', 'DIFFERENT'];

/**
 * Bandeau d'une page de config commune : où la config est lue, où elle sera écrite, et quels
 * containers en diffèrent aujourd'hui. Rien à choisir : enregistrer aligne tous les containers, sauf
 * sur les sections `untouched` que la page n'écrit pas (absentes de la référence), dites à part.
 */
export function ContainerScope({
  entries,
  referencePath,
  labelOf,
  untouched = [],
}: {
  entries: ScopeEntry[];
  /** Fichier lu dans la référence, quand il y en a un. */
  referencePath?: string | null;
  /** Libellé d'une section ou d'un champ (sinon la clé brute). */
  labelOf?: (key: string) => string;
  /** Sections que l'enregistrement ne crée ni ne retire : chaque container garde la sienne. */
  untouched?: string[];
}) {
  const ref = entries.find((e) => e.status === 'REFERENCE');
  if (!ref) return null;
  const carriers = entries.filter((e) => CARRIES.includes(e.status));
  const differing = entries.filter((e) => e.status === 'DIFFERENT');
  const skipped = entries.filter((e) => !CARRIES.includes(e.status));
  const label = (k: string) => labelOf?.(k) ?? k;
  const kept = (k: string) => untouched.includes(k);
  // Un container qui diffère sans détail est aligné par l'enregistrement (la page écrit tout ce qu'elle gère).
  const aligns = differing.some((e) => !e.differs?.length || e.differs.some((k) => !kept(k)));
  const keptKeys = [...new Set(differing.flatMap((e) => (e.differs ?? []).filter(kept)))];

  return (
    <div className={styles.scope}>
      <p className={styles.scopeLine}>
        <Server size={14} aria-hidden />
        <span>
          Configuration commune : affichée depuis <strong>{ref.containerName}</strong>
          {referencePath && <> (<code className={styles.code}>{referencePath}</code>)</>}, enregistrée dans{' '}
          {carriers.length === 1
            ? 'ce seul container'
            : <>les {carriers.length} containers qui la portent ({carriers.map((e) => e.containerName).join(', ')})</>}.
        </span>
      </p>
      {differing.length > 0 && (
        <div className={styles.diverge} role="status">
          <AlertTriangle size={14} aria-hidden />
          <div>
            <strong>Containers non alignés.</strong> {differing.length > 1 ? 'Ces containers diffèrent' : 'Ce container diffère'} de{' '}
            {ref.containerName} :
            <ul className={styles.divergeList}>
              {differing.map((e) => (
                <li key={e.containerId}>
                  <strong>{e.containerName}</strong>
                  {e.differs && e.differs.length > 0 && <> : {e.differs.map(label).join(', ')}</>}
                </li>
              ))}
            </ul>
            {aligns && "Enregistrer écrit la configuration affichée dans chacun d'eux."}
            {keptKeys.length > 0 && (
              <>
                {aligns && ' '}
                {keptKeys.length > 1 ? 'Sections absentes' : 'Section absente'} de {ref.containerName}, que la page ne
                crée ni ne retire : {keptKeys.map(label).join(', ')}. Chaque container garde la sienne, à aligner à la
                main dans Paramètres généraux → Containers.
              </>
            )}
          </div>
        </div>
      )}
      {skipped.length > 0 && (
        <p className={styles.skipped}>
          Rien n'y sera écrit : {skipped.map((e) => `${e.containerName} (${SKIP_LABEL[e.status] ?? e.status})`).join(', ')}.
        </p>
      )}
    </div>
  );
}

/**
 * Statuts d'un container qui ne porte tout simplement pas le fichier (par opposition à un fichier
 * inutilisable). Un mod absent de l'image en fait partie : son fichier n'y est pas, même quand un
 * autre mod y lit un fichier du même nom.
 */
const ABSENT: ConfigInspectStatus[] = ['SKIPPED_FILE_MISSING', 'SKIPPED_NOT_SYNCED', 'SKIPPED_MOD_ABSENT'];

/** Vrai quand le fichier manque partout ; faux s'il est présent quelque part mais illisible ou ambigu. */
export function isAbsentEverywhere(entries: Array<{ status: ConfigInspectStatus }>): boolean {
  return entries.every((e) => ABSENT.includes(e.status));
}

/**
 * Lecture faite, mais aucun container n'a pu servir de référence : le titre dit si le fichier manque
 * partout ou s'il est présent mais inutilisable (plusieurs candidats, JSON invalide), puis le pourquoi
 * container par container.
 */
export function NoReference({
  file,
  entries,
  mod,
}: {
  file: string;
  entries: Array<{ containerId: string; containerName: string; status: ConfigInspectStatus; message: string }>;
  /** Mod propriétaire du fichier, quand la page le désigne par son mod et non par son seul nom. */
  mod?: string;
}) {
  const absent = isAbsentEverywhere(entries);
  return (
    <>
      <AlertTriangle size={22} />
      {absent && mod ? (
        <p>Aucun container du projet ne porte <strong>{mod}</strong> avec son fichier <code>{file}</code>.</p>
      ) : absent ? (
        <p>Fichier <code>{file}</code> absent de tous les containers du projet.</p>
      ) : (
        <p>Fichier <code>{file}</code> présent, mais inutilisable dans chaque container qui le porte.</p>
      )}
      <p className={styles.skipped}>
        {absent && mod
          ? `${mod} est cherché dans le release-version.json de chaque image, et son fichier directement dans le dossier des mods, là où il le lit. Un container qui vient d'être déclaré se synchronise depuis Paramètres généraux → Containers.`
          : absent
          ? "Recherche faite dans toute l'arborescence de chaque container. Un container qui vient d'être déclaré se synchronise depuis Paramètres généraux → Containers."
          : "Rien n'est lu ni écrit à l'aveugle : corrige le fichier dans le container concerné (Paramètres généraux → Containers), puis rafraîchis."}
      </p>
      <MissingList entries={entries} />
    </>
  );
}

/** Aucun container ne fournit la config : pourquoi, un container par ligne. */
export function MissingList({ entries }: { entries: Array<{ containerId: string; containerName: string; message: string }> }) {
  if (entries.length === 0) return null;
  return (
    <ul className={styles.missingList}>
      {entries.map((e) => <li key={e.containerId}><strong>{e.containerName}</strong> : {e.message}</li>)}
    </ul>
  );
}

const APPLY_LABEL: Record<ContainerApplyStatus, string> = {
  WRITTEN: 'Écrit',
  CREATED: 'Créé',
  UNCHANGED: 'Déjà identique',
  SKIPPED_NOT_SYNCED: 'Non synchronisé',
  SKIPPED_FILE_MISSING: 'Config absente',
  SKIPPED_AMBIGUOUS: 'Ambigu',
  SKIPPED_INVALID_JSON: 'JSON invalide',
  SKIPPED_EXISTS: 'Existe déjà',
  SKIPPED_MOD_ABSENT: 'Mod absent',
  SKIPPED_MOD_SHARED: 'Lu par deux mods',
  SKIPPED_MOD_UNKNOWN: 'Mod non identifié',
  FAILED: 'Échec',
};

function statusClass(s: ContainerApplyStatus): string {
  if (s === 'WRITTEN' || s === 'CREATED') return styles.statusOk;
  if (s === 'FAILED') return styles.statusBad;
  return styles.statusSkip;
}

/** Rapport d'un enregistrement : container par container, ce qui a été écrit, ignoré ou raté. */
export function ApplyReport({ result, onClose }: { result: ContainerApplyResult; onClose?: () => void }) {
  return (
    <div className={`${styles.report} ${result.success ? styles.reportOk : styles.reportBad}`}>
      <div className={styles.reportHead}>
        {result.success ? <Check size={14} aria-hidden /> : <AlertTriangle size={14} aria-hidden />}
        <span>{result.message}</span>
        {onClose && (
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Masquer le rapport">
            <X size={14} />
          </button>
        )}
      </div>
      {result.entries.length > 0 && (
        <div className={styles.reportRows}>
          {result.entries.map((e) => (
            <div key={e.containerId} className={styles.reportRow}>
              <span className={`${styles.status} ${statusClass(e.status)}`}>{APPLY_LABEL[e.status] ?? e.status}</span>
              <span className={styles.reportName}>{e.containerName}</span>
              <span className={styles.reportDetail}>
                {e.path && <code className={styles.code}>{e.path}</code>} {e.message}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Rapport construit côté panel, pour les réglages enregistrés container par container (grades) :
 * même lecture que celui du serveur pour les pages de fichiers.
 */
export function applyResultOf(entries: ContainerApplyEntry[]): ContainerApplyResult {
  const count = (pred: (e: ContainerApplyEntry) => boolean) => entries.filter(pred).length;
  const changed = count((e) => e.status === 'WRITTEN' || e.status === 'CREATED');
  const unchanged = count((e) => e.status === 'UNCHANGED');
  const failed = count((e) => e.status === 'FAILED');
  const skipped = entries.length - changed - unchanged - failed;
  const message = changed + unchanged + failed === 0
    ? "Rien n'a été écrit : aucun container du projet ne porte cette config."
    : `${changed} container(s) mis à jour`
      + (unchanged > 0 ? `, ${unchanged} déjà identique(s)` : '')
      + (skipped > 0 ? `, ${skipped} ignoré(s)` : '')
      + (failed > 0 ? `, ${failed} en échec` : '')
      + ". Rien n'a été publié : envoie les containers avec le bouton Envoyer.";
  return { success: failed === 0 && changed + unchanged > 0, message, sourcePath: null, keys: [], entries };
}
